import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import ReactApexChart from 'react-apexcharts';
import { ContentContainer, Pills, Pill } from 'components';
import { colors } from 'style/theme';
import { Select } from 'antd';
import NoDataChart from './NoDataChart';

const ChartWrapper = styled.div`
  position: relative;
  width: 98%;
`;

const ChartFilters = styled.div({
  position: 'absolute',
  height: 32,
  padding: 5,
  fontSize: 18,
  background: colors.brandWhite,
  right: '0',
  top: '0',
  zIndex: 5,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  width: '100%'
});

const generateSeries = (data) => {
  const relevantValues = ['pages', 'errors', 'scans'];
  const filtered = Object.entries(data).filter(([key, value]) => relevantValues.includes(key));
  const series = [];
  filtered.forEach(([key, value]) => {
    series.push({
      name: key,
      data: value
    });
  });
  return series;
};

const getOptions = (dates) => {
  return {
    chart: {
      id: 'statistics',
      fontFamily: '"Inter"',
      stacked: true,
      toolbar: {
        show: true,
        offsetX: 20,
        offsetY: -10,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    grid: {
      padding: {
        top: 40
      }
    },
    colors: [colors.warning.color, colors.brand, colors.success.color],
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    markers: {
      colors: '#fff',
      strokeColors: [colors.warning.color, colors.brand, colors.success.color],
      size: 4,
      hover: {
        sizeOffset: 3
      }
    },
    legend: {
      show: false
    },
    xaxis: {
      type: 'category',
      categories: dates
    },
    yaxis: {
      tickAmount: 1,
      labels: {
        formatter: (value) => value.toFixed(0)
      }
    }
  };
};

const FILTERS = {
  ERRORS: 'errors',
  PAGES: 'pages',
  SCANS: 'scans'
};

const getFormatDateFunction = (group) => (date) => {
  const configurationLookUpTable = {
    week: { month: 'numeric', day: 'numeric', year: '2-digit' },
    month: { month: 'long', year: 'numeric' },
    year: { year: 'numeric' }
  };

  const getTimeZoneOffsetDate = (date) => {
    const offsetDate = new Date(date.getTime());
    offsetDate.setHours(date.getHours() + date.getTimezoneOffset() / 60);
    return offsetDate;
  };

  const getEndOfWeekDate = (date) => {
    const endOfWeekDate = new Date(date.getTime());
    endOfWeekDate.setDate(date.getDate() + 6);
    return endOfWeekDate;
  };
  const offsetDate = getTimeZoneOffsetDate(new Date(date));

  const formatDateToString = (date) => date.toLocaleDateString('en-us', configurationLookUpTable[group] || {});

  return `${formatDateToString(offsetDate)}${
    group === 'week' ? ` - ${formatDateToString(getEndOfWeekDate(offsetDate))}` : ''
  }`;
};

function MainChart({ filters, chartData, setFilters }) {
  const [staticSeries, setStaticSeries] = useState();
  const [series, setSeries] = useState();
  const [options, setOptions] = useState();
  const [chartFilters, setChartFilters] = useState(FILTERS.ERRORS);

  const filterSeries = (value, seriesData) => {
    seriesData = seriesData || staticSeries;
    const newSeries = seriesData.filter((s) => value === s.name);
    setChartFilters(value);
    setSeries(newSeries);
  };

  useEffect(() => {
    if (filters && chartData) {
      const seriesData = generateSeries(chartData);
      filterSeries(chartFilters, seriesData);
      setStaticSeries(seriesData);
      const formatDate = getFormatDateFunction(filters.group);
      setOptions(getOptions(chartData.dates.map(formatDate)));
    }
  }, [chartData]);

  const onDateChange = (value) => {
    setFilters({ ...filters, group: value });
  };

  return (
    <ContentContainer>
      {series && (
        <ChartWrapper>
          <ChartFilters>
            <Select
              onChange={(value) => filterSeries(value)}
              defaultValue={FILTERS.ERRORS}
              showArrow={true}
              size="small"
              // style={{  }}
              placeholder="SELECT FILTER"
            >
              <Select.Option value={FILTERS.ERRORS}>ERRORS</Select.Option>
              <Select.Option value={FILTERS.PAGES}>PAGES</Select.Option>
              <Select.Option value={FILTERS.SCANS}>SCANS</Select.Option>
            </Select>
            <Pills onChange={onDateChange} value={filters.group}>
              <Pill value="day">Daily</Pill>
              <Pill value="week">Weekly</Pill>
              <Pill value="month">Monthly</Pill>
              <Pill value="year">Yearly</Pill>
            </Pills>
          </ChartFilters>
          <ReactApexChart options={options} series={series} type="area" height="300" />
        </ChartWrapper>
      )}
      {!series && (
        <ChartWrapper>
          <NoDataChart />
        </ChartWrapper>
      )}
    </ContentContainer>
  );
}

export default MainChart;
